/**
 * Settings → Account → Sign-in and security.
 *
 * Everything here belongs to the person looking at it, which is why it is open to every
 * portal user regardless of permissions: nobody needs an administrator's say-so to add
 * a passkey to their own account or change their own password.
 *
 * Talks to Cognito directly through Amplify with the person's own access token. There
 * is no API route in the middle, and deliberately so — the backend never sees a
 * password or a passkey, which is one fewer place either could leak from.
 */

import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import {
  associateWebAuthnCredential,
  deleteWebAuthnCredential,
  fetchMFAPreference,
  listWebAuthnCredentials,
  setUpTOTP,
  updateMFAPreference,
  updatePassword,
  verifyTOTPSetup,
  type AuthWebAuthnCredential,
} from 'aws-amplify/auth';

import { Card } from '@/components/ui';
import { useAuth } from '@/features/auth/AuthProvider';
import { authErrorCode, authErrorKey } from '@/features/auth/errors';
import { useConfirm } from '@/components/ConfirmDialog';
import { useI18n } from '@/i18n';
import { dateShort } from '@/lib/format';

const passkeysSupported = typeof window !== 'undefined' && 'PublicKeyCredential' in window;

export function AccountSecurity() {
  const { t } = useI18n();
  /*
   * Shared by the two rows below because they constrain each other. Amazon Cognito does
   * not offer passkey sign-in to anyone who has the authenticator app on (in a pool where
   * MFA is optional, "configured MFA" means password sign-in only) — so a passkey added
   * next to the app registered fine and then "did not exist" at every sign-in. Each row
   * now says so, and offers the fix.
   */
  const [totpOn, setTotpOn] = useState<boolean | null>(null);
  const [passkeyCount, setPasskeyCount] = useState(0);

  useEffect(() => {
    void fetchMFAPreference()
      .then((p) => setTotpOn(Boolean(p.enabled?.includes('TOTP'))))
      .catch(() => setTotpOn(false));
  }, []);

  return (
    <Card>
      <h2 className="font-bold">{t('account.security')}</h2>
      <p className="mt-1 max-w-xl text-xs leading-relaxed text-muted">{t('account.securityHint')}</p>

      <div className="mt-4 divide-y divide-border border-y border-border">
        <Passkeys totpOn={totpOn === true} onTotpOff={() => setTotpOn(false)} onCount={setPasskeyCount} />
        <AuthenticatorApp on={totpOn} setOn={setTotpOn} passkeyCount={passkeyCount} />
        <ChangePassword />
      </div>
    </Card>
  );
}

/** A row in the security list: what it is, its state, and what you can do about it. */
function Row({
  title,
  hint,
  status,
  action,
  children,
}: {
  title: string;
  hint: string;
  status?: ReactNode;
  action?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <section className="py-4">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold">{title}</h3>
            {status}
          </div>
          <p className="mt-0.5 max-w-lg text-xs leading-relaxed text-muted">{hint}</p>
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      {children}
    </section>
  );
}

function StatusPill({ on, label }: { on: boolean; label: string }) {
  return (
    <span
      className={[
        'rounded-full px-2 py-0.5 text-[11px] font-semibold',
        on
          ? 'bg-[var(--color-success-bg)] text-[var(--color-success)]'
          : 'bg-surface-alt text-subtle',
      ].join(' ')}
    >
      {label}
    </span>
  );
}

function Message({ tone, children }: { tone: 'ok' | 'error'; children: ReactNode }) {
  return (
    <p
      role={tone === 'error' ? 'alert' : 'status'}
      className={`mt-3 text-sm ${tone === 'error' ? 'text-danger' : 'text-success font-semibold'}`}
    >
      {children}
    </p>
  );
}

// --- Passkeys ------------------------------------------------------------------

function Passkeys({
  totpOn,
  onTotpOff,
  onCount,
}: {
  totpOn: boolean;
  onTotpOff: () => void;
  onCount: (n: number) => void;
}) {
  const { t } = useI18n();
  const confirm = useConfirm();
  const [list, setList] = useState<AuthWebAuthnCredential[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const load = async () => {
    try {
      const credentials = (await listWebAuthnCredentials()).credentials;
      setList(credentials);
      onCount(credentials.length);
    } catch (e) {
      setList([]);
      setError(t(authErrorKey(e)));
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const add = async () => {
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      await associateWebAuthnCredential();
      setOk(t('account.passkeyAdded'));
      await load();
    } catch (e) {
      console.error('[passkey] registration failed', e);
      const code = authErrorCode(e);
      setError(`${t(authErrorKey(e))}${code ? ` (${code})` : ''}`);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (credentialId: string) => {
    if (!(await confirm({ message: t('account.removeConfirm'), confirmLabel: t('common.remove'), danger: true }))) return;
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      await deleteWebAuthnCredential({ credentialId });
      await load();
    } catch (e) {
      setError(t(authErrorKey(e)));
    } finally {
      setBusy(false);
    }
  };

  const count = list?.length ?? 0;

  const turnOffApp = async () => {
    setBusy(true);
    setError(null);
    try {
      await updateMFAPreference({ totp: 'DISABLED' });
      onTotpOff();
      setOk(t('account.passkeyReady'));
    } catch (e) {
      setError(t(authErrorKey(e)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Row
      title={t('account.passkeys')}
      hint={passkeysSupported ? t('account.passkeysHint') : t('account.unsupported')}
      status={list ? <StatusPill on={count > 0} label={count > 0 ? String(count) : t('account.off')} /> : null}
      action={
        passkeysSupported ? (
          <button type="button" className="btn-primary text-sm" disabled={busy} onClick={() => void add()}>
            {t('account.addPasskey')}
          </button>
        ) : null
      }
    >
      {count > 0 ? (
        <ul className="mt-3 space-y-2">
          {list?.map((p) => (
            <li
              key={p.credentialId}
              className="flex items-center justify-between gap-3 rounded-lg bg-surface-alt px-3 py-2"
            >
              <div className="min-w-0">
                {/* The name comes from the device's password manager — "iCloud Keychain",
                    "Windows Hello", "Google Password Manager" — which is exactly how
                    someone recognises which one to remove. */}
                <div className="truncate text-sm font-medium">
                  {p.friendlyCredentialName || t('account.unnamed')}
                </div>
                {p.createdAt ? (
                  <div className="text-xs text-subtle">
                    {t('account.addedOn', { date: dateShort(p.createdAt.toISOString()) })}
                  </div>
                ) : null}
              </div>
              <button
                type="button"
                disabled={busy}
                onClick={() => p.credentialId && void remove(p.credentialId)}
                className="text-xs font-semibold text-danger hover:underline disabled:opacity-60"
              >
                {t('account.remove')}
              </button>
            </li>
          ))}
        </ul>
      ) : list ? (
        <p className="mt-2 text-xs text-subtle">{t('account.noPasskeys')}</p>
      ) : null}
      {count > 0 && totpOn ? (
        <div className="mt-3 rounded-lg border border-[var(--color-warning)] bg-warning-soft px-3 py-2.5">
          <p className="text-xs leading-relaxed text-text">{t('account.passkeyBlockedByApp')}</p>
          <button
            type="button"
            className="btn-secondary mt-2 text-xs"
            disabled={busy}
            onClick={() => void turnOffApp()}
          >
            {t('account.turnOffAppForPasskey')}
          </button>
        </div>
      ) : null}
      {error ? <Message tone="error">{error}</Message> : null}
      {ok && !(count > 0 && totpOn) ? <Message tone="ok">{ok}</Message> : null}
    </Row>
  );
}

// --- Authenticator app ---------------------------------------------------------

function AuthenticatorApp({
  on,
  setOn,
  passkeyCount,
}: {
  on: boolean | null;
  setOn: (on: boolean) => void;
  passkeyCount: number;
}) {
  const { t } = useI18n();
  const ask = useConfirm();
  const { email } = useAuth();
  const [setup, setSetup] = useState<{ uri: string; secret: string } | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const start = async () => {
    // The trade has to be a choice, not a surprise at the next sign-in.
    if (passkeyCount > 0 && !(await ask({ message: t('account.appDisablesPasskeys') }))) return;
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      const details = await setUpTOTP();
      setSetup({
        uri: details.getSetupUri('HH Electrical Systems', email ?? undefined).toString(),
        secret: details.sharedSecret,
      });
    } catch (e) {
      setError(t(authErrorKey(e)));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await verifyTOTPSetup({ code: code.trim() });
      await updateMFAPreference({ totp: 'PREFERRED' });
      setOn(true);
      setSetup(null);
      setCode('');
      setOk(t('account.enabled'));
    } catch (err) {
      setError(t(authErrorKey(err)));
    } finally {
      setBusy(false);
    }
  };

  const turnOff = async () => {
    if (!(await ask({ message: t('account.turnOffConfirm'), danger: true }))) return;
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      await updateMFAPreference({ totp: 'DISABLED' });
      setOn(false);
      setOk(t('account.disabled'));
    } catch (e) {
      setError(t(authErrorKey(e)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Row
      title={t('account.app')}
      hint={t('account.appHint')}
      status={on === null ? null : <StatusPill on={on} label={on ? t('account.on') : t('account.off')} />}
      action={
        on === null || setup ? null : on ? (
          <button type="button" className="btn-secondary text-sm" disabled={busy} onClick={() => void turnOff()}>
            {t('account.turnOff')}
          </button>
        ) : (
          <button type="button" className="btn-secondary text-sm" disabled={busy} onClick={() => void start()}>
            {t('account.setUp')}
          </button>
        )
      }
    >
      {setup ? (
        <form onSubmit={confirm} className="mt-3 flex flex-col gap-4 rounded-lg bg-surface-alt p-4 sm:flex-row sm:items-start">
          <QrCode text={setup.uri} />
          <div className="min-w-0 flex-1 space-y-3">
            <p className="text-xs leading-relaxed text-muted">{t('auth.setupMfa.subtitle')}</p>
            <p className="break-all font-mono text-xs text-text">{setup.secret}</p>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              placeholder="000000"
              aria-label={t('auth.mfa.code')}
              className="input w-40 text-center font-mono tracking-[0.3em]"
            />
            <div className="flex gap-2">
              <button type="submit" className="btn-primary text-sm" disabled={busy || code.length < 6}>
                {t('auth.setupMfa.confirm')}
              </button>
              <button
                type="button"
                className="btn-secondary text-sm"
                disabled={busy}
                onClick={() => {
                  setSetup(null);
                  setCode('');
                }}
              >
                {t('account.cancel')}
              </button>
            </div>
          </div>
        </form>
      ) : null}
      {error ? <Message tone="error">{error}</Message> : null}
      {ok ? <Message tone="ok">{ok}</Message> : null}
    </Row>
  );
}

// --- Password ------------------------------------------------------------------

function ChangePassword() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setOk(null);
    if (newPassword !== confirmPassword) {
      setError(t('account.mismatch'));
      return;
    }
    setBusy(true);
    try {
      await updatePassword({ oldPassword, newPassword });
      setOk(t('account.passwordChanged'));
      setOpen(false);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(t(authErrorKey(err)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Row
      title={t('account.password')}
      hint={t('account.passwordHint')}
      action={
        open ? null : (
          <button type="button" className="btn-secondary text-sm" onClick={() => setOpen(true)}>
            {t('account.changePassword')}
          </button>
        )
      }
    >
      {open ? (
        <form onSubmit={submit} className="mt-3 grid max-w-md gap-3 rounded-lg bg-surface-alt p-4">
          <input
            type="password"
            className="input"
            autoComplete="current-password"
            placeholder={t('account.currentPassword')}
            aria-label={t('account.currentPassword')}
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            required
          />
          <input
            type="password"
            className="input"
            autoComplete="new-password"
            placeholder={t('account.newPassword')}
            aria-label={t('account.newPassword')}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            minLength={12}
            required
          />
          <input
            type="password"
            className="input"
            autoComplete="new-password"
            placeholder={t('account.confirmPassword')}
            aria-label={t('account.confirmPassword')}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            minLength={12}
            required
          />
          <div className="flex gap-2">
            <button type="submit" className="btn-primary text-sm" disabled={busy}>
              {t('account.changePassword')}
            </button>
            <button type="button" className="btn-secondary text-sm" disabled={busy} onClick={() => setOpen(false)}>
              {t('account.cancel')}
            </button>
          </div>
        </form>
      ) : null}
      {error ? <Message tone="error">{error}</Message> : null}
      {ok ? <Message tone="ok">{ok}</Message> : null}
    </Row>
  );
}

/**
 * The QR for an authenticator app, drawn in the browser — the secret must never go to
 * an image service. Same approach as the sign-in page.
 */
function QrCode({ text }: { text: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    void import('qrcode').then(async (qr) => {
      const url = await qr.toDataURL(text, { margin: 1, width: 160 });
      if (!cancelled) setSrc(url);
    });
    return () => {
      cancelled = true;
    };
  }, [text]);

  if (!src) return <div className="h-[160px] w-[160px] shrink-0 rounded-lg bg-surface" />;
  return <img src={src} alt="" width={160} height={160} className="shrink-0 rounded-lg bg-white p-2" />;
}
